import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Checkbox } from '@mui/material';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import * as S from 'commons/components/Table/Table.styles';
import { STODocumentStatusEnum } from 'commons/enums';
import { STODocument } from 'commons/types';
import { RowTitleProps } from 'commons/types/listPageData.interface';
import { TableActionRow, DataTable } from 'pages/dashboard/commons/components';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import { RowMenuItemBulkConfig } from '../configs';
import { STOListTableProps } from '../interfaces';
import { ISTOBulkDialogData } from '../types/sto-dialog-state.interface';

import BulkActionBtnSTOMenu from './BulkActionBtnMenu.component';
import STOItemDialogPage from './STOItemDialogPage.component';
import STORowActionBtn from './STORowActionBtn.component';

const rowTitles: RowTitleProps[] = [
  { title: 'Document Number', sortField: 'sto_number' },
  { title: 'Status' },
  { title: 'Total QTY' },
  { title: 'Total SKU(s)' },
  { title: 'From Warehouse' },
  { title: 'To Warehouse' },
  { title: 'Flag Reason' },
];

const Row: React.FC<{
  data: STODocument;
  index: number;
  selectedLocation?: number;
  fromWarehouseName: string;
  toWarehouseName: string;
  onCheckboxClick: (poNumber: string) => void;
  checked: boolean;
  handleSetSTOItemDetail: (value: STODocument) => void;
}> = ({
  data,
  index,
  selectedLocation,
  fromWarehouseName,
  toWarehouseName,
  onCheckboxClick,
  checked,
  handleSetSTOItemDetail,
}) => {
  return (
    <S.TableRow>
      <S.TableColumn style={{ width: '5%' }}>
        <Checkbox
          data-testid={`STOListCheckBox{${index + 1}}`}
          onClick={() => onCheckboxClick(data.sto_number)}
          checked={checked}
        />
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '20%' }}
        data-testid={`STOListDocumentNumber${index + 1}`}
      >
        {data.sto_number}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '10%', textTransform: 'capitalize' }}
        data-testid={`STOListStatus-${index + 1}`}
      >
        {data.status.replace(/_/g, ' ')}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '10%' }}
        data-testid={`STOListTotalQTY-${index + 1}`}
      >
        {data.total_qty || '-'}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '10%' }}
        data-testid={`STOListTotalSKU-${index + 1}`}
      >
        {data.total_sku || '-'}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '15%' }}
        data-testid={`STOListFromWarehouse-${index + 1}`}
      >
        {data.from_warehouse === selectedLocation ? (
          <p style={{ fontWeight: '700' }}>{fromWarehouseName}</p>
        ) : (
          fromWarehouseName
        )}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '15%' }}
        data-testid={`STOListToWarehouse-${index + 1}`}
      >
        {data.to_warehouse === selectedLocation ? (
          <p style={{ fontWeight: '700' }}>{toWarehouseName}</p>
        ) : (
          toWarehouseName
        )}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '15%' }}
        data-testid={`STOListFlagReason-${index + 1}`}
      >
        {data.status === STODocumentStatusEnum.SHIP_UPDATE_FAILED &&
        data.related_document?.error_history
          ? data.related_document?.error_history[
              data.related_document.error_history.length - 1
            ].message
          : ''}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '20%', textAlign: 'right' }}
        data-testid={`STOListRowActionBtn-${index + 1}`}
      >
        <Button
          data-testid={`buttonSTOListRetry-{${index + 1}}`}
          onClick={() => handleSetSTOItemDetail(data)}
          variant="outlined"
          sx={{
            width: 87,
          }}
        >
          Details
        </Button>
        <STORowActionBtn
          data-testid={`buttonSTORowAction-{${index + 1}}`}
          stoDocument={data}
          selectedLocation={selectedLocation}
        />
      </S.TableColumn>
    </S.TableRow>
  );
};

const STOListTable: React.FC<STOListTableProps> = (params): React.ReactNode => {
  const { data = [] }: STOListTableProps = params;
  const { selectedLocation, activeLocations } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );
  const dispatch = useDispatch<AppDispatch>();
  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [checkedOrders, setCheckedOrders] = useState<string[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [openDialogData, setOpenDialogData] =
    useState<ISTOBulkDialogData | null>(null);
  const [showDetailSTODocument, setShowDetailSTODocument] =
    useState<STODocument | null>(null);

  const selectedDocs = data.filter((task) =>
    checkedOrders.includes(task.sto_number),
  );
  const selectedDocNumbers = selectedDocs.map((order) => order.sto_number);

  const handleCheckOrder = (stoNumber: string): void => {
    if (checkedOrders.includes(stoNumber)) {
      // remove stoNumber from checkedOrders
      checkedOrders.splice(checkedOrders.indexOf(stoNumber), 1);
      setCheckedOrders([...checkedOrders]);
    } else {
      // insert sto number to checkedOrders
      setCheckedOrders([...checkedOrders, stoNumber]);
    }
  };

  const handleCheckAll = (): void => {
    if (checkAll) {
      // remove all check list
      setCheckedOrders([]);
    } else {
      // add all sto number to checkedOrders
      setCheckedOrders(data.map((doc) => doc.sto_number));
    }
    setCheckAll(!checkAll);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const handleClickMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = (): void => {
    setAnchorEl(null);
  };
  const handleOpenDialog = (name: string) => {
    setOpenDialogData(
      RowMenuItemBulkConfig.find((config) => config.title === name) || null,
    );
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpenDialogData(null);
    setOpen(false);
  };

  const handleSetSTOItemDetail = (value: STODocument | null) => {
    setShowDetailSTODocument(value);
  };

  return (
    <>
      <TableActionRow
        dataName="sto docs"
        selectedData={selectedDocs}
        anchorEl={anchorEl}
        openMenu={openMenu}
        handleClickMenu={handleClickMenu}
      >
        <BulkActionBtnSTOMenu
          selectedDocuments={selectedDocs}
          handleCloseMenu={handleCloseMenu}
          handleOpen={handleOpenDialog}
        />
      </TableActionRow>
      <DataTable
        rowTitles={rowTitles}
        checkAll={checkAll}
        handleCheckAll={handleCheckAll}
      >
        <S.TableBody>
          {data.map((item, index) => (
            <Row
              data={item}
              index={index}
              selectedLocation={selectedLocation?.warehouse_id}
              onCheckboxClick={handleCheckOrder}
              checked={checkedOrders.includes(item.sto_number)}
              handleSetSTOItemDetail={handleSetSTOItemDetail}
              fromWarehouseName={
                activeLocations.find(
                  (location) => location.warehouse_id === item.from_warehouse,
                )?.abbreviation || ''
              }
              toWarehouseName={
                activeLocations.find(
                  (location) => location.warehouse_id === item.to_warehouse,
                )?.abbreviation || ''
              }
            />
          ))}
        </S.TableBody>
      </DataTable>

      {data.length > 0 && (
        <TableActionRow
          dataName="sto docs"
          selectedData={selectedDocs}
          anchorEl={anchorEl}
          openMenu={openMenu}
          handleClickMenu={handleClickMenu}
        >
          <BulkActionBtnSTOMenu
            selectedDocuments={selectedDocs}
            handleCloseMenu={handleCloseMenu}
            handleOpen={handleOpenDialog}
          />
        </TableActionRow>
      )}

      <ConfirmationDialog
        title={openDialogData?.title || ''}
        content={openDialogData?.content(selectedDocNumbers) || ''}
        onClose={() => handleCloseDialog()}
        openModal={open}
        onConfirm={() =>
          openDialogData?.onConfirm(
            dispatch,
            selectedDocNumbers,
            selectedLocation?.warehouse_id,
          )
        }
      />

      <STOItemDialogPage
        stoDocument={showDetailSTODocument}
        onClose={() => handleSetSTOItemDetail(null)}
      />
    </>
  );
};

export default STOListTable;
